import { label } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Row, Small } from '../styles';
import { choice, dateField, textField } from '../types';
import { PagedTable } from '../components/CollectionTable';

export interface RecoveryTabProps {
  workspace: KspWorkspace;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  createDocument: (kind: string) => void;
  workOptions: { value: string; label: string }[];
  fileOptions: { value: string; label: string }[];
}

export function RecoveryTab({
  workspace: d,
  permission,
  open,
  execute,
  createDocument,
  workOptions,
  fileOptions,
}: RecoveryTabProps) {
  return (
    <Card>
      <Row>
        <h3>Планы ликвидации отставания</h3>
        <Button
          disabled={!permission() || !d.summary}
          onClick={() => createDocument('recovery_plan')}
        >
          Создать план
        </Button>
      </Row>
      {d.documents
        .filter((document) => document.kind === 'recovery_plan')
        .map((document) => (
          <Card key={document.id}>
            <h3>{document.title}</h3>
            <Muted>
              {label(document.status)} · снимок {document.snapshot_id}
            </Muted>
            <Button
              disabled={!permission() || document.status !== 'draft'}
              onClick={() =>
                open(
                  'Добавить мероприятие',
                  [
                    textField('title', 'Мероприятие'),
                    choice('activity_id', 'Работа', workOptions),
                    textField('responsible_id', 'Ответственный'),
                    dateField('due_date', 'Срок'),
                    textField('resources', 'Ресурсы'),
                    textField('expected_effect', 'Ожидаемый эффект'),
                    choice('source_file_id', 'Документ основания', fileOptions),
                  ],
                  (values) =>
                    execute(`recovery-plans/${document.id}/actions`, {
                      ...values,
                      resources: [
                        {
                          description: values.resources,
                          expected_effect: values.expected_effect,
                        },
                      ],
                    }),
                )
              }
            >
              Мероприятие
            </Button>
            <Button
              disabled={!permission('approve') || document.status !== 'draft'}
              onClick={() =>
                open(
                  'Согласовать план ликвидации',
                  [
                    choice(
                      'evidence_file_id',
                      'Документ согласования Заказчика',
                      fileOptions,
                    ),
                    textField('basis', 'Основание'),
                  ],
                  (values) => execute(`recovery-plans/${document.id}/approve`, values),
                )
              }
            >
              Согласовать
            </Button>
            <PagedTable
              rows={document.actions ?? []}
              columns={[
                { name: 'Мероприятие', value: (action) => action.title },
                {
                  name: 'Ответственный / срок',
                  value: (action) => `${action.responsible_id} · ${action.due_date}`,
                },
                {
                  name: 'Ресурсы / эффект',
                  value: (action) => (
                    <>
                      {Array.isArray(action.resources)
                        ? action.resources
                            .map((resource) =>
                              typeof resource === 'string'
                                ? resource
                                : (resource.description ?? ''),
                            )
                            .filter(Boolean)
                            .join(', ')
                        : action.resources}
                      <Small>
                        {action.expected_effect ||
                          (Array.isArray(action.resources)
                            ? action.resources
                                .map((resource) =>
                                  typeof resource === 'string'
                                    ? ''
                                    : (resource.expected_effect ?? ''),
                                )
                                .filter(Boolean)
                                .join('; ')
                            : '')}{' '}
                        · {action.evidence}
                      </Small>
                    </>
                  ),
                },
                { name: 'Выполнение', value: (action) => label(action.status) },
              ]}
            />
          </Card>
        ))}
      <Muted>
        Мероприятия изменяют сценарий прогноза. Старый договорной baseline и принятый факт
        сохраняются.
      </Muted>
    </Card>
  );
}
